/**
 * SSO state storage abstraction.
 *
 * On Capacitor (mobile), OAuth opens Chrome Custom Tabs via Browser.open().
 * The round-trip through Chrome Custom Tabs + deep link back to the app
 * can lose sessionStorage. We use localStorage on Capacitor instead,
 * with immediate cleanup after reading to avoid stale state.
 *
 * On web, sessionStorage is used (more secure, auto-clears on tab close).
 */

import { isCapacitor } from '../api/config';

const SSO_KEYS = [
  'sso_state',
  'sso_redirect_uri',
  'sso_provider',
  'sso_invitation_token',
  'sso_invitation_tenant_id',
  'sso_invitation_tenant_name',
  'sso_return_to',
] as const;

type SSOKey = typeof SSO_KEYS[number];

// On Capacitor, use localStorage because sessionStorage is unreliable
// during OAuth flows that navigate away and return via deep links.
const storage = isCapacitor ? localStorage : sessionStorage;

export function setSSOItem(key: SSOKey, value: string): void {
  storage.setItem(key, value);
}

export function getSSOItem(key: SSOKey): string | null {
  return storage.getItem(key);
}

export function removeSSOItem(key: SSOKey): void {
  storage.removeItem(key);
}

/** Remove all SSO state keys from storage. */
export function clearSSOState(): void {
  for (const key of SSO_KEYS) {
    storage.removeItem(key);
  }
}
